/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Button, FormControl, InputLabel, OutlinedInput, Typography } from '@mui/material';

import { Languages } from 'shared';

import { DeletableThumbnailBadge } from '../../../../components/DeletableThumbnailBadge/DeletableThumbnailBadge';
import { EDIT_PHOTO_ACCEPTABLE_FILES } from '../../../../components/EditPhoto/EditPhoto';
import { EditPhotoModal } from '../../../../components/EditPhotoModal/EditPhotoModal';
import { EntityThumbnail } from '../../../../components/EntityThumbnail/EntityThumbnail';
import { ErrorMessageWithIcon } from '../../../../components/ErrorMessageWithIcon/ErrorMessageWithIcon';
import { HUSpin } from '../../../../components/HUSpin/HUSpin';
import { PageOverlay } from '../../../../components/PageOverlay/PageOverlay';
import { SimpleErrorModal } from '../../../../components/SimpleErrorModal/SimpleErrorModal';
import { Namespaces } from '../../../../i18n';
import { ConvertibleCanvas } from '../../../../miscellaneous/ConvertibleCanvas';
import { SuperuserDatasetCategoryApiService } from '../../../../services/SuperuserDatasetCategoryApiService';
import { useFormStyles } from '../../../../styles/form';
import { ApiClientError } from '../../../../types/api/ApiClientError';
import { SUDatasetCategory } from '../../../../types/superuser/dataset-category/SUDatasetCategory';
import { SUDatasetCategoryCreateRequest } from '../../../../types/superuser/dataset-category/SUDatasetCategoryCreateRequest';
import { SUDatasetCategoryI18nRequest } from '../../../../types/superuser/dataset-category/SUDatasetCategoryI18n';
import { SUDatasetCategoryUpdateRequest } from '../../../../types/superuser/dataset-category/SUDatasetCategoryUpdateRequest';
import { reactToUpdateAdminDatasetCategories } from '../../../../utils/reactions';
import { useApiValidation } from '../../../../utils/useApiValidation';
import { useFileInputChange } from '../../../../utils/useFileInputChange';
import { ignoreApiErrors, validationSchema } from './EditDatasetCategoryForm.consts';
import { useEditDatasetCategoryFormStyles } from './EditDatasetCategoryForm.styles';
import { EditDatasetCategoryFormValues } from './EditDatasetCategoryForm.types';

export type EditDatasetCategoryFormType = {
  isOpen: boolean;
  closeOverlay: () => void;
  datasetCategory: SUDatasetCategory | null;
};

export function EditDatasetCategoryForm(props: EditDatasetCategoryFormType) {
  const { isOpen, closeOverlay, datasetCategory } = props;
  const formClasses = useFormStyles();
  const classes = useEditDatasetCategoryFormStyles();
  const { t } = useTranslation(Namespaces.Admin);

  const isEditing = useMemo(() => !!datasetCategory, [datasetCategory]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [editImageSrc, setEditImageSrc] = useState<string>('');
  const [openThumbnailModal, setOpenThumbnailModal] = useState<boolean>(false);
  const [displayableThumb, setDisplayableThumb] = useState<string>('');

  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  const { resetApiError, setApiError, getFieldApiError, hasFieldApiError, resetFieldApiError } = useApiValidation();

  const hasError = (field: 'thumbnail' | 'identifier'): boolean => {
    return !!touched[field] && (!!errors[field] || hasFieldApiError(field));
  };

  const getError = (field: 'thumbnail' | 'identifier'): string => {
    return errors[field] || getFieldApiError(field);
  };

  const initialValues = useMemo<EditDatasetCategoryFormValues>(() => {
    const i18n: SUDatasetCategoryI18nRequest[] =
      datasetCategory?.i18n.map(l => {
        return {
          title: l.title,
          language: l.language,
        };
      }) || [];

    Languages.forEach(language => {
      if (i18n.findIndex(i => i.language === language) === -1) {
        i18n.push({ language, title: '' });
      }
    });
    return {
      i18n,
      thumbnail: datasetCategory?.thumbnail ?? '',
      identifier: datasetCategory?.identifier ?? '',
    };
  }, [datasetCategory]);

  const trimNameValues = (i18n: SUDatasetCategoryI18nRequest[]) => {
    return i18n.map(lang => {
      return {
        ...lang,
        title: lang.title.trim(),
      };
    });
  };

  const onSubmit = async (values: EditDatasetCategoryFormValues) => {
    try {
      setIsLoading(true);
      resetApiError();
      if (isEditing && datasetCategory) {
        const requestBody: SUDatasetCategoryUpdateRequest = {
          ...values,
          i18n: trimNameValues(values.i18n),
        };
        await SuperuserDatasetCategoryApiService.updateDatasetCategory({
          datasetCategoryId: datasetCategory.id,
          data: requestBody,
        });
      } else {
        const requestBody: SUDatasetCategoryCreateRequest = {
          ...values,
          i18n: trimNameValues(values.i18n),
        };
        await SuperuserDatasetCategoryApiService.createDatasetCategory({
          data: requestBody,
        });
      }
      await reactToUpdateAdminDatasetCategories();
      setIsLoading(false);
      setEditImageSrc('');
      setDisplayableThumb('');
      closeOverlay();
    } catch (e) {
      const error = e as ApiClientError;
      const message = error.response?.data.message;
      setIsLoading(false);
      if (ignoreApiErrors.indexOf(message as string) === -1) {
        setIsOpenErrorModal(true);
      }
      if (error.response) {
        setApiError(error);
      }
    }
  };

  const formik = useFormik<EditDatasetCategoryFormValues>({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const {
    setFieldTouched,
    setFieldValue,
    handleSubmit,
    values,
    handleChange,
    dirty,
    errors,
    touched,
    handleBlur,
    resetForm,
  } = formik;

  const { onFileInputChange } = useFileInputChange((imageDataUrl: string) => {
    setEditImageSrc(imageDataUrl);
    setOpenThumbnailModal(true);
  });

  const onCancel = () => {
    setEditImageSrc('');
    setDisplayableThumb('');
    setOpenThumbnailModal(false);
  };

  const onSave = async (canvas: ConvertibleCanvas) => {
    const thumbnail = await canvas.toBase64();
    onCancel();
    void setFieldTouched('thumbnail');
    void setFieldValue('thumbnail', thumbnail);
    setDisplayableThumb(thumbnail);
  };

  const handleCloseOverlayForm = () => {
    resetForm();
    resetApiError();
    setDisplayableThumb('');
    closeOverlay();
  };

  const getI18nError = (index: number) => {
    return !!_.get(errors, ['i18n', index, 'title']);
  };

  const hasI18nError = (index: number) => {
    return !!_.get(touched, ['i18n', index, 'title']) && getI18nError(index);
  };

  return (
    <PageOverlay
      open={isOpen}
      classes={{ childrenBlock: 'd-flex' }}
      showCloseBtn
      onCloseBtnClick={handleCloseOverlayForm}
      disableRestoreFocus
      name="edit-dataset-category"
    >
      <div className={classes.formRoot}>
        <Typography variant={'h2'} className={classes.title}>
          {isEditing ? t('datasets.categories.form.edit_title') : t('datasets.categories.form.add_title')}
        </Typography>
        <HUSpin spinning={isLoading} delay={50}>
          <form onSubmit={handleSubmit}>
            <div className={classes.thumbnailRoot}>
              {isEditing ? (
                <DeletableThumbnailBadge
                  className={classes.thumbnailWrap}
                  overlap="circular"
                  invisible={values.thumbnail === datasetCategory?.thumbnail}
                  onDelBtnClick={() => {
                    setDisplayableThumb('');
                    void setFieldValue('thumbnail', datasetCategory?.thumbnail);
                    void setFieldTouched('thumbnail', false);
                  }}
                  thumbnail={
                    <EntityThumbnail
                      allowUploadImage={false}
                      size={50}
                      type="custom_src"
                      src={displayableThumb || values.thumbnail}
                      className={classes.thumbnail}
                    />
                  }
                />
              ) : (
                displayableThumb && (
                  <div className={classes.thumbnailWrap} data-testid="thumb_wrap_test">
                    <EntityThumbnail
                      allowUploadImage={false}
                      size={50}
                      type="custom_src"
                      src={displayableThumb}
                      className={classes.thumbnail}
                    />
                  </div>
                )
              )}
              <FormControl>
                <Button className={classes.uploadThumbnail} variant="outlined">
                  <label htmlFor="file_input">{t('datasets.categories.form.upload_thumbnail')}</label>
                </Button>
                <input
                  data-testid="upload_thumb"
                  id="file_input"
                  name="thumbnail"
                  type="file"
                  accept={EDIT_PHOTO_ACCEPTABLE_FILES}
                  className={classes.fileChooser}
                  onChange={onFileInputChange}
                />
                {hasError('thumbnail') && (
                  <ErrorMessageWithIcon
                    message={t(`datasets.categories.form.errors.thumbnail.${getError('thumbnail')!}`)}
                    className={classes.pl16}
                  />
                )}
              </FormControl>
            </div>
            {values.i18n.map((lang, key) => {
              const name: string = `i18n[${key}].title`;
              return (
                <FormControl
                  key={key}
                  className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
                  variant="outlined"
                  error={hasI18nError(key)}
                >
                  <InputLabel htmlFor={name}>
                    {t('datasets.categories.form.name', {
                      currentLanguage: lang.language,
                    })}
                  </InputLabel>
                  <OutlinedInput
                    inputProps={{ 'data-testid': `category_name_${key}` }}
                    name={name}
                    label={t('datasets.categories.form.name', {
                      currentLanguage: lang.language,
                    })}
                    fullWidth
                    type="text"
                    value={lang.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  {hasI18nError(key) && (
                    <ErrorMessageWithIcon
                      message={t('datasets.categories.form.errors.name.required', {
                        i18n: lang.language,
                      })}
                    />
                  )}
                </FormControl>
              );
            })}
            <FormControl
              className={cx(classes.formControl, formClasses.formControl, formClasses.widthFull)}
              variant="outlined"
            >
              <InputLabel htmlFor="identifier">{t('datasets.categories.form.identifier')}</InputLabel>
              <OutlinedInput
                id="identifier"
                name="identifier"
                label={t('datasets.categories.form.identifier')}
                fullWidth
                type="text"
                value={values.identifier}
                onChange={e => {
                  resetFieldApiError('identifier');
                  handleChange(e);
                }}
                onBlur={handleBlur}
                error={hasError('identifier')}
              />
              {hasError('identifier') && (
                <ErrorMessageWithIcon
                  message={t(`datasets.categories.form.errors.identifier.${getError('identifier')!}`)}
                />
              )}
            </FormControl>
            {isEditing && <Alert severity="warning">{t('datasets.categories.form.warnings.identifier')}</Alert>}
            <Button variant="contained" className={classes.buttonApply} type="submit" disabled={!dirty}>
              {isEditing ? t('datasets.categories.form.save') : t('datasets.categories.form.add')}
            </Button>
          </form>
        </HUSpin>
        <EditPhotoModal
          open={openThumbnailModal}
          imageSrc={editImageSrc}
          onSave={onSave}
          onCancel={onCancel}
          size="big"
        />
        <SimpleErrorModal
          open={isOpenErrorModal}
          message={t(`${Namespaces.Common}:errors.unexpected_error`)}
          onOk={() => {
            setIsOpenErrorModal(false);
            resetApiError();
          }}
        />
      </div>
    </PageOverlay>
  );
}
